import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { api } from '@/api/client'
import { Button } from '@/components/ui'
import type { RoomWidget } from './RoomContent'
import styles from './RoomContent.module.css'

export function UrlWidget({ roomId, widget }: { roomId: string; widget: RoomWidget }): ReactElement {
  const host = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [reload, setReload] = useState(0)
  const active = widget.enabled !== false && (widget.lazy_load === false || visible)
  useEffect(() => {
    if (!host.current) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const observer = new IntersectionObserver(entries => setVisible(entries.some(entry => entry.isIntersecting)))
    observer.observe(host.current)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    if (!active || !visible || !widget.cache_seconds) return
    const timer = setInterval(() => { if (document.visibilityState === 'visible') setReload(value => value + 1) }, Math.max(30, widget.cache_seconds) * 1000)
    return () => clearInterval(timer)
  }, [active, visible, widget.cache_seconds])
  const src = `${api.getBasePath()}/rooms/${encodeURIComponent(roomId)}/widgets/${encodeURIComponent(widget.id)}/render?v=${widget.revision}&r=${reload}`
  const frameStyle = { width: '100%', maxWidth: widget.max_width ?? undefined, maxHeight: widget.max_height ?? undefined,
    height: widget.aspect_ratio ? undefined : widget.height ?? 360, aspectRatio: widget.aspect_ratio?.replace(':', ' / ') }
  return <div ref={host}>
    {widget.enabled === false ? <p>This widget is disabled.</p> : <div className={styles.embedFrame} style={frameStyle}>{active ? <iframe key={src} src={src} title={widget.title}
      sandbox="allow-scripts" referrerPolicy="no-referrer" className={styles.embed} />
      : <div className={styles.embedPlaceholder}>Loads when visible</div>}</div>}
    {active ? <Button size="sm" variant="ghost" onClick={() => setReload(value => value + 1)}>Reload widget</Button> : null}
  </div>
}
